import { supabase } from '../lib/supabase';

export interface Transaction {
  id: string;
  user_id: string | null;
  paypal_order_id: string | null;
  transaction_type: 'payment' | 'refund' | 'test';
  is_test_payment: boolean;
  product_id: string | null;
  order_id: string | null;
  vendor_email: string;
  platform_email: string;
  vendor_amount: number;
  platform_amount: number;
  total_amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  paypal_status: string | null;
  payment_method: string;
  payer_id: string | null;
  payer_email: string | null;
  metadata: any;
  notes: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  cancelled_at: string | null;
}

export interface CreateTransactionData {
  user_id?: string;
  paypal_order_id?: string;
  transaction_type?: 'payment' | 'refund' | 'test';
  is_test_payment?: boolean;
  product_id?: string | null;
  order_id?: string | null;
  vendor_email: string;
  platform_email: string;
  vendor_amount: number;
  platform_amount: number;
  total_amount: number;
  currency?: string;
  status?: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  paypal_status?: string;
  payment_method?: string;
  payer_id?: string;
  payer_email?: string;
  metadata?: any;
  notes?: string;
}

export interface UpdateTransactionData {
  status?: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  paypal_status?: string;
  paypal_order_id?: string;
  payer_id?: string;
  payer_email?: string;
  metadata?: any;
  notes?: string;
  completed_at?: string;
  cancelled_at?: string;
}

export class TransactionService {
  /**
   * Create a new transaction
   */
  static async createTransaction(data: CreateTransactionData): Promise<Transaction> {
    try {
      // Get current user if not provided
      const { data: { user } } = await supabase.auth.getUser();
      const userId = data.user_id || user?.id || null;

      const transactionData = {
        user_id: userId,
        paypal_order_id: data.paypal_order_id || null,
        transaction_type: data.transaction_type || 'payment',
        is_test_payment: data.is_test_payment ?? false,
        product_id: data.product_id || null,
        order_id: data.order_id || null,
        vendor_email: data.vendor_email,
        platform_email: data.platform_email,
        vendor_amount: data.vendor_amount,
        platform_amount: data.platform_amount,
        total_amount: data.total_amount,
        currency: data.currency || 'USD',
        status: data.status || 'pending',
        paypal_status: data.paypal_status || null,
        payment_method: data.payment_method || 'paypal',
        payer_id: data.payer_id || null,
        payer_email: data.payer_email || null,
        metadata: data.metadata || null,
        notes: data.notes || null,
      };

      const { data: transaction, error } = await supabase
        .from('transactions')
        .insert(transactionData)
        .select()
        .single();

      if (error) {
        console.error('Error creating transaction:', error);
        throw error;
      }

      return transaction;
    } catch (error) {
      console.error('Error in createTransaction:', error);
      throw error;
    }
  }

  /**
   * Update a transaction
   */
  static async updateTransaction(
    transactionId: string,
    data: UpdateTransactionData
  ): Promise<Transaction> {
    try {
      const updateData: any = { ...data };

      // Set completed_at if status is completed
      if (data.status === 'completed' && !data.completed_at) {
        updateData.completed_at = new Date().toISOString();
      }

      // Set cancelled_at if status is cancelled
      if (data.status === 'cancelled' && !data.cancelled_at) {
        updateData.cancelled_at = new Date().toISOString();
      }

      const { data: transaction, error } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('id', transactionId)
        .select()
        .single();

      if (error) {
        console.error('Error updating transaction:', error);
        throw error;
      }

      return transaction;
    } catch (error) {
      console.error('Error in updateTransaction:', error);
      throw error;
    }
  }

  /**
   * Update transaction by PayPal order ID
   */
  static async updateTransactionByPayPalOrderId(
    paypalOrderId: string,
    data: UpdateTransactionData
  ): Promise<Transaction | null> {
    try {
      const updateData: any = { ...data };

      if (data.status === 'completed' && !data.completed_at) {
        updateData.completed_at = new Date().toISOString();
      }

      if (data.status === 'cancelled' && !data.cancelled_at) {
        updateData.cancelled_at = new Date().toISOString();
      }

      const { data: transaction, error } = await supabase
        .from('transactions')
        .update(updateData)
        .eq('paypal_order_id', paypalOrderId)
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows found
          return null;
        }
        console.error('Error updating transaction by PayPal order ID:', error);
        throw error;
      }

      return transaction;
    } catch (error) {
      console.error('Error in updateTransactionByPayPalOrderId:', error);
      throw error;
    }
  }

  /**
   * Get transaction by ID
   */
  static async getTransaction(transactionId: string): Promise<Transaction | null> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', transactionId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error getting transaction:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getTransaction:', error);
      throw error;
    }
  }

  /**
   * Get transaction by PayPal order ID
   */
  static async getTransactionByPayPalOrderId(
    paypalOrderId: string
  ): Promise<Transaction | null> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('paypal_order_id', paypalOrderId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error getting transaction by PayPal order ID:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getTransactionByPayPalOrderId:', error);
      throw error;
    }
  }

  /**
   * Get all transactions for the current user
   */
  static async getUserTransactions(): Promise<Transaction[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error getting user transactions:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserTransactions:', error);
      throw error;
    }
  }

  /**
   * Get transactions for a specific product
   */
  static async getProductTransactions(productId: string): Promise<Transaction[]> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error getting product transactions:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getProductTransactions:', error);
      throw error;
    }
  }

  /**
   * Get transactions by order ID (purchase order ID)
   */
  static async getTransactionsByOrderId(orderId: string): Promise<Transaction[]> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error getting transactions by order ID:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getTransactionsByOrderId:', error);
      throw error;
    }
  }

  /**
   * Get all transactions (admin only - bypasses RLS)
   */
  static async getAllTransactions(): Promise<Transaction[]> {
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error getting all transactions:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllTransactions:', error);
      throw error;
    }
  }
}

